import { useEffect, useMemo, useCallback } from "react";

import { isNotEqualDeep, isNotEmpty, noop } from "neetocist";
import { isEmpty, pluck, prop } from "ramda";

import {
  useFetchTableConfiguration,
  useUpdateTableConfiguration,
  useDestroyTableConfiguration,
} from "./useTableConfigurationsApi.js";

const useTableConfigurations = (
  tableKey,
  columnData,
  onMutationSuccess = noop,
  allowConfigurationResetOnUpdate = true,
  defaultHiddenColumns = []
) => {
  const {
    data: tableConfiguration,
    isLoading,
    isFetching,
  } = useFetchTableConfiguration({ tableKey });

  const { mutate: updateConfiguration } = useUpdateTableConfiguration(
    tableKey,
    { onSuccess: onMutationSuccess }
  );

  const { mutate: destroyConfiguration } = useDestroyTableConfiguration(
    tableKey,
    { onSuccess: onMutationSuccess }
  );

  const processColumnsWithConfiguration = useCallback(
    (originalColumns = [], existingColumnConfiguration = []) => {
      if (isEmpty(existingColumnConfiguration)) {
        return originalColumns;
      }

      const originalColumnsByKey = new Map(
        originalColumns?.filter(prop("key")).map(column => [column.key, column])
      );

      const configurationByKey = new Map(
        existingColumnConfiguration.map(config => [config.key, config])
      );

      const orderedConfiguredColumns = existingColumnConfiguration
        .map(config => originalColumnsByKey.get(config.key))
        .filter(Boolean);

      const unconfiguredOriginalColumns = originalColumns.filter(
        column => column.key && !configurationByKey.has(column.key)
      );

      return [...orderedConfiguredColumns, ...unconfiguredOriginalColumns];
    },
    []
  );

  const hasAdditionalColumns = useMemo(() => {
    if (!tableConfiguration?.columns || !columnData) {
      return false;
    }

    const configurationKeys = pluck("key", tableConfiguration.columns).sort();
    const dataKeys = pluck("key", columnData).sort();

    return isNotEqualDeep(configurationKeys, dataKeys);
  }, [tableConfiguration, columnData]);

  const shouldApplyTableConfiguration = useMemo(
    () =>
      !isLoading &&
      tableConfiguration?.columns &&
      isNotEmpty(tableConfiguration?.columns) &&
      !hasAdditionalColumns,
    [tableConfiguration, isLoading, hasAdditionalColumns]
  );

  useEffect(() => {
    if (
      hasAdditionalColumns &&
      tableConfiguration &&
      allowConfigurationResetOnUpdate
    ) {
      destroyConfiguration(tableKey);
    }
  }, [hasAdditionalColumns, tableKey, allowConfigurationResetOnUpdate]);

  const allProcessedColumns = useMemo(() => {
    if (
      !isLoading &&
      isNotEmpty(tableConfiguration?.columns) &&
      !hasAdditionalColumns
    ) {
      return processColumnsWithConfiguration(
        columnData,
        tableConfiguration?.columns
      );
    }

    return columnData || [];
  }, [
    isLoading,
    tableConfiguration?.columns,
    hasAdditionalColumns,
    columnData,
    processColumnsWithConfiguration,
  ]);

  const existingColumnConfiguration = useMemo(() => {
    if (shouldApplyTableConfiguration) {
      return tableConfiguration.columns;
    }

    if (!columnData || isEmpty(columnData)) {
      return [];
    }

    return columnData.map(column => ({
      key: column.key,
      isShown: !defaultHiddenColumns.includes(column.key),
    }));
  }, [
    shouldApplyTableConfiguration,
    columnData,
    tableConfiguration?.columns,
    defaultHiddenColumns,
  ]);

  const customizedColumns = useMemo(() => {
    const visibleColumnsConfig = existingColumnConfiguration.filter(
      prop("isShown")
    );
    const visibleColumnKeys = pluck("key", visibleColumnsConfig);

    return allProcessedColumns.filter(column =>
      visibleColumnKeys.includes(column.key)
    );
  }, [allProcessedColumns, existingColumnConfiguration]);

  const updateColumns = updatedColumns => {
    updateConfiguration({
      tableKey,
      payload: { table_key: tableKey, columns: updatedColumns },
    });
  };

  const hideColumn = columnKey => {
    updateColumns(
      existingColumnConfiguration.map(column =>
        column.key === columnKey ? { ...column, isShown: false } : column
      )
    );
  };

  const showColumn = columnKey => {
    updateColumns(
      existingColumnConfiguration.map(column =>
        column.key === columnKey ? { ...column, isShown: true } : column
      )
    );
  };

  return {
    customizedColumns,
    allProcessedColumns,
    isTableConfigurationLoading: isLoading,
    isTableConfigurationFetching: isFetching,
    existingColumnConfiguration,
    hideColumn,
    showColumn,
    updateColumns,
    destroyConfiguration,
  };
};

export default useTableConfigurations;
